
// Libraries
import dayjs from "dayjs";
import { Component, namespace, Vue } from "nuxt-property-decorator";
import BreadCrumb from "~/components/molecules/display/BreadCrumb.vue";
import LoadingSpinner from "~/components/molecules/display/LoadingSpinner.vue";
import PartnerTile from "~/components/molecules/text/PartnerTile.vue";
import PriceChart from "~/components/organisms/chart/PriceChart.vue";
import PriceTrendChart from "~/components/organisms/chart/PriceTrendChart.vue";
import DataTile from "~/components/organisms/display/DataTile.vue";
import PriceComparisonHistoricalData from "~/components/organisms/display/PriceComparisonHistoricalData.vue";
import OilImportTable from "~/components/organisms/table/OilImportTable.vue";
import { generateMetaTags } from "~/operations/meta-helper-functions";
import { decodeUrlForPlace } from "~/operations/shared";
import { BreadCrumbDetail } from "~/types/BreadCrumb";
import { HistoricalOilPriceData } from "~/types/HistoricalOilPriceData";
import { calculateHistoricalStatistics } from "~/utils/calculateStatistics";

import { getHistoricalData } from "~/utils/calculateStatistics";

import {
  formatNumberAsPercentage,
  returnPriceFormatDE,
  returPriceTrendFormatDE,
  translateStateName,
  translateStateToNutsCode,
} from "~/mixins/mixins";

import { createBreadCrumbsStructuredData } from "~/mixins/breadcrumb";
import { Descriptions } from "~/operations/messages";

const OilpriceModule = namespace("oilprice");

const FederalstatesModule = namespace("federalstates");

@Component({
  components: {
    DataTile,
    PriceTrendChart,
    PriceChart,
    OilImportTable,
    PriceComparisonHistoricalData,
    PartnerTile,
    BreadCrumb,
    LoadingSpinner,
  },
  methods: {
    translateStateToNutsCode,
    translateStateName,
    decodeUrlForPlace,
  },
})
export default class StatesRedirectView extends Vue {
  head() {
    return {
      ...generateMetaTags(this.$route.path, "heating-oil-overview"),

      script: [
        {
          json: {
            "@context": "http://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: createBreadCrumbsStructuredData(
              this.breadCrumbData,
            ),
          },
          type: "application/ld+json",
        },
      ],
    };
  }

  @OilpriceModule.State("assets")
  assets: any;

  @OilpriceModule.Action("fetchHistoricalOilPrices")
  fetchHistoricalOilPrices: any;

  @OilpriceModule.State("cheapestCities")
  cheapestCities: any;

  @OilpriceModule.State("nationYearStatistics")
  nationYearStatistics: any;

  @OilpriceModule.State("stateAssetsPrice")
  stateAssetsPrice: any;

  @OilpriceModule.State("cheapestCounties")
  cheapestCounties: any;

  @FederalstatesModule.State("topStatePartners")
  topStatePartners: any;

  Descriptions = Descriptions;

  async fetch() {
    await Promise.all([
      this.fetchPriceTrendData(),
      this.fetchCityPriceTrends(),
      this.fetchHistoricalOilPrices(),
      this.$store.dispatch("schwarmometer/fetchUserSignal"),
      this.$store.dispatch("schwarmometer/fetchEsyoilSignal", ""),
      this.fetchDistrictPriceTrends(),
      this.fetchCheapestCities(),
      this.fetchCheapestCounties(),
      this.fetchPartnersAndRatingyByZipcode(),
    ]);
  }

  async fetchDistrictPriceTrends() {
    await this.$store.dispatch("oilprice/fetchDistrictPriceTrend");
  }

  async fetchPriceTrendData() {
    await this.$store.dispatch("oilprice/fetchNationalPriceTrend");
  }

  fetchTopCities(stateName) {
    this.$store.dispatch("federalstates/fetchTopStateCities", stateName);
  }

  async fetchCheapestCities() {
    await this.$store.dispatch("oilprice/fetchCheapestCities");
  }

  async fetchCheapestCounties() {
    await this.$store.dispatch("oilprice/fetchCheapestCounties");
  }

  async fetchPartnersAndRatingyByZipcode() {
    await this.$store.dispatch(
      "federalstates/fetchPartnersAndRatingyByZipcode",
      "00000",
    );
  }

  async fetchCityPriceTrends() {
    await this.$store.dispatch("oilprice/fetchAverageCityPrices");
  }

  generateTableHeaderData(mainColumnTitle, timestamps = []) {
    const formattedDates = timestamps.map((timestamp) =>
      dayjs.unix(timestamp).format("DD.MM"),
    );

    if (formattedDates.length === 0) {
      const today = dayjs();
      const yesterday = today.subtract(1, "day");

      formattedDates.push(today.format("DD.MM"));
      formattedDates.push(yesterday.format("DD.MM"));
    }

    const headerData = [
      mainColumnTitle,
      `Preis am ${formattedDates[0]}`,
      `Preis am ${formattedDates[1]}`,
      "Differenz/Trend",
    ];

    return headerData;
  }

  formatTableRowData(items, options = {} as any) {
    const {
      sortKey = "currentPrice.value",
      hrefTemplate,
      includeNumber = true,
      differenceCalculation = (item) => item.current - item.previous,
    } = options;

    const sortedItems = [...items].sort((a, b) => {
      const aValue =
        sortKey.split(".").reduce((acc, key) => acc?.[key], a) ?? 0;
      const bValue =
        sortKey.split(".").reduce((acc, key) => acc?.[key], b) ?? 0;
      return aValue - bValue;
    });

    return sortedItems.map((item, index) => {
      const difference = differenceCalculation(item);

      return [
        includeNumber
          ? { number: index + 1, text: item.name, href: hrefTemplate(item) }
          : { text: item.name, href: hrefTemplate(item) },
        returnPriceFormatDE(
          sortKey.split(".").reduce((acc, key) => acc?.[key], item) ?? 0,
        ),
        returnPriceFormatDE(
          item.previousPrice ??
            item.lastPrice?.value ??
            item.price?.previous ??
            item.yesterday ??
            item.previous ??
            0,
        ),
        returPriceTrendFormatDE(difference),
      ];
    });
  }

  returnStateTableHeaderData(state) {
    return this.generateTableHeaderData("Bundesland", [
      state[0]?.currentPrice?.modifiedAt ?? null,
      state[0]?.lastPrice?.modifiedAt ?? null,
    ]);
  }

  formatTopStatesTableData(state) {
    return this.formatTableRowData(state, {
      hrefTemplate: (item) => {
        const specialStates = ["bremen", "berlin", "hamburg"];
        const lowerCaseName = item.name.toLowerCase();

        return specialStates.includes(lowerCaseName)
          ? `/heizoelpreise/heizoelpreise-${lowerCaseName}-${item.zipcode}`
          : item.link;
      },
      differenceCalculation: (item) =>
        item.currentPrice.value - item.lastPrice.value,
    });
  }
  formatBigCitiesTableData(state) {
    return this.formatTableRowData(state.priceData, {
      sortKey: "price.actual",
      hrefTemplate: (item) =>
        decodeUrlForPlace(`heizoelpreise-${item.seoName}-${item.zipcode}`),
      differenceCalculation: (item) => item.price.actual - item.price.previous,
      includeNumber: false,
    });
  }

  formatCheapestCitiesTable(state) {
    return this.formatTableRowData(state, {
      sortKey: "price",
      hrefTemplate: (item) =>
        decodeUrlForPlace(`heizoelpreise-${item.name}-${item.zipcode}`),
      differenceCalculation: (item) => item.price - item.yesterday,
    });
  }

  formatCheapestCountiesTable(state) {
    return this.formatTableRowData(state.data ?? [], {
      sortKey: "current",
      hrefTemplate: (item) =>
        `heizoelpreise/bundeslaender/${item.federalStateName.toLowerCase()}/${item.name.toLowerCase()}`,
      differenceCalculation: (item) => item.current - item.previous,
    });
  }

  get nationWideHistoricalData() {
    return calculateHistoricalStatistics(this.assets.data);
  }

  get historicalHighestData(): HistoricalOilPriceData[] {
    return getHistoricalData(this.nationWideHistoricalData, "highest");
  }

  get historicalLowestData(): HistoricalOilPriceData[] {
    return getHistoricalData(this.nationWideHistoricalData, "lowest");
  }

  get breadCrumbData(): BreadCrumbDetail[] {
    return [
      {
        lastItem: true,
        linkUrl: "/heizoelpreise",
        linkValue: "/heizoelpreise",
      },
    ];
  }

  get todaysPrice() {
    return returnPriceFormatDE(this.stateAssetsPrice.currentPrice.value);
  }

  get priceOneWeekAgo() {
    const oneWeekAgoTimestamp = dayjs().subtract(7, "day").unix();
    const recentPrice = this.assets.data
      .filter((asset) => asset.changedAtUnix <= oneWeekAgoTimestamp)
      .sort((a, b) => b.changedAtUnix - a.changedAtUnix)[0];
    return recentPrice ? recentPrice.priceData : null;
  }

  get percentageDifference(): number | null {
    if (this.priceOneWeekAgo === null) {
      return null;
    }
    return (
      ((this.stateAssetsPrice.currentPrice.value - this.priceOneWeekAgo) /
        this.priceOneWeekAgo) *
      100
    );
  }

  get displayText(): string {
    const formattedPercentage = formatNumberAsPercentage(
      Number(this.percentageDifference),
    );

    if (this.stateAssetsPrice.currentPrice.value < this.priceOneWeekAgo) {
      return `Damit kann man Heizöl ${formattedPercentage} günstiger kaufen als in der Vorwoche.`;
    } else {
      return `Damit ist Heizöl ${formattedPercentage} teurer als in der Vorwoche.`;
    }
  }
}
